// "Top N make X% of the total" above a ranked list — RetailMaxx's share control (src/client/ui/share.tsx), ported. Both
// questions at once, the numbers the person's to change (kept on this device per list), and the list can be cut to either
// answer. Shares are of what the rows shown add up to; a row with no figure is left out and said so, never counted as 0.
// The working is in share-math.ts (the same as RetailMaxx's shared/share.ts).
import React from 'react';
import { inr } from '../api';
import { curveOf, cutReach, cutTop, leftOutText, rankKeys, reachText, topText, type ShareCurve, type ShareWords } from './share-math';

export interface ShareSpec<R> {
  /** Names this list, so its numbers are kept on this device. */
  id: string;
  /** The amount a row adds to the total (null: no figure). */
  value(r: R): number | null | undefined;
  /** What the rows are called ("dealer", "dealers") and what the amount is ("sales", "stock"); money by default. */
  one: string; many: string; measure?: string; fmt?: (n: number) => string; verb?: ShareWords['verb'];
}
type Only = 'all' | 'top' | 'reach';
const TOPS = [5, 10, 20, 50], PCTS = [50, 80, 90];
interface Kept { n: number; pct: number }
const read = (id: string): Kept => {
  try { const v = JSON.parse(localStorage.getItem('leo-share:' + id) ?? 'null') as Partial<Kept> | null; return { n: Number(v?.n) > 0 ? Number(v?.n) : 10, pct: Number(v?.pct) > 0 ? Number(v?.pct) : 50 }; }
  catch { return { n: 10, pct: 50 }; }
};
const write = (id: string, k: Kept) => { try { localStorage.setItem('leo-share:' + id, JSON.stringify(k)); } catch { /* storage off */ } };

export interface ShareCut {
  /** The rows the chosen cut keeps (null: every row). */
  keep: Set<string> | null;
  /** A line for the export saying the cut ("Only the top 10 dealers"). */
  note: string | null;
  view: { curve: ShareCurve; words: ShareWords; kept: Kept; set: (k: Partial<Kept>) => void; only: Only; setOnly: (o: Only) => void; top: ReturnType<typeof cutTop>; reach: ReturnType<typeof cutReach>; topKeep: boolean; reachKeep: boolean; count: number } | null;
}

/** The share of the rows given (after the filter box), and the cut chosen. Call it whether or not the list has a share. */
export function useShareCut<R extends { key: string }>(rows: readonly R[], spec: ShareSpec<R> | undefined): ShareCut {
  const [kept, setKept] = React.useState<Kept & { id?: string }>(() => (spec ? { ...read(spec.id), id: spec.id } : { n: 10, pct: 50 }));
  const [only, setOnly] = React.useState<Only>('all');
  if (spec && kept.id !== spec.id) setKept({ ...read(spec.id), id: spec.id });
  const calc = React.useMemo(() => {
    if (!spec) return null;
    const curve = curveOf(rows.map(r => spec.value(r)));
    const keys = rankKeys(rows, r => spec.value(r), r => r.key);
    return { curve, keys };
  }, [rows, spec]);
  if (!spec || !calc || calc.curve.pos.length < 2) return { keep: null, note: null, view: null };
  const words: ShareWords = { one: spec.one, many: spec.many, fmt: spec.fmt ?? (n => inr(n)), measure: spec.measure ?? 'sales', ...(spec.verb ? { verb: spec.verb } : {}) };
  const { curve, keys } = calc;
  const top = cutTop(curve, kept.n), reach = cutReach(curve, kept.pct);
  const set = (k: Partial<Kept>) => { const next = { n: kept.n, pct: kept.pct, ...k }; setKept({ ...next, id: spec.id }); write(spec.id, next); };
  // a cut is offered only when it leaves rows out, and never one to nothing
  const ok = (n: number, has: boolean) => has && n > 0 && n < rows.length;
  const topKeep = ok(top.n, top.share !== null), reachKeep = ok(reach.n, reach.share !== null);
  const sel: Only = only === 'top' && topKeep ? 'top' : only === 'reach' && reachKeep ? 'reach' : 'all';
  const keep = sel === 'top' ? new Set(keys.slice(0, top.n)) : sel === 'reach' ? new Set(keys.slice(0, reach.n)) : null;
  const note = sel === 'top' ? `Only the top ${top.n} ${words.many}: ${topText(curve, top, words)}` : sel === 'reach' ? `Only the ${reach.n} ${words.many} making ${kept.pct}% of the total: ${reachText(curve, reach, kept.pct, words)}` : null;
  return { keep, note, view: { curve, words, kept, set, only: sel, setOnly, top, reach, topKeep, reachKeep, count: rows.length } };
}

/** The bar: "Top [10] … bring ₹x: y% of ₹z" and "[50]% of total: n of m … make up 50%", with the cut buttons. */
export function ShareBar({ cut }: { cut: ShareCut }) {
  const v = cut.view; if (!v) return null;
  const { curve, words, kept, set, only, setOnly, top, reach, topKeep, reachKeep } = v;
  const left = leftOutText(curve, words);
  return <div className="share-bar" aria-label={`Share of ${words.measure ?? 'sales'}`} role="group">
    <div className="share-line">
      <span className="share-ask">Top <NumIn value={kept.n} max={9999} label={`How many ${words.many} at the top`} onChange={n => set({ n })} /></span>
      <span className="share-quick">{TOPS.map(t => <button type="button" key={t} className={kept.n === t ? 'on' : ''} onClick={() => set({ n: t })}>{t}</button>)}</span>
      <span className="share-says">{topText(curve, top, words)}</span>
    </div>
    <div className="share-line">
      <span className="share-ask"><NumIn value={kept.pct} max={100} label="What share of the total" onChange={pct => set({ pct })} />% of total</span>
      <span className="share-quick">{PCTS.map(p => <button type="button" key={p} className={kept.pct === p ? 'on' : ''} onClick={() => set({ pct: p })}>{p}%</button>)}</span>
      <span className="share-says">{reachText(curve, reach, kept.pct, words)}</span>
    </div>
    {(topKeep || reachKeep || left) && <div className="share-foot">
      {(topKeep || reachKeep) && <div className="seg" role="radiogroup" aria-label="Show in the list">
        <button type="button" role="radio" aria-checked={only === 'all'} onClick={() => setOnly('all')}>All {words.many}</button>
        {topKeep && <button type="button" role="radio" aria-checked={only === 'top'} onClick={() => setOnly('top')}>Only the top {top.n}</button>}
        {reachKeep && <button type="button" role="radio" aria-checked={only === 'reach'} onClick={() => setOnly('reach')}>Only the {reach.n} making {kept.pct}%</button>}
      </div>}
      {left && <span className="small muted">{left}</span>}
    </div>}
  </div>;
}

/** A whole number from 1 to max; what is typed stays while it is changed, each whole number in range is taken at once. */
function NumIn({ value, max, label, onChange }: { value: number; max: number; label: string; onChange(n: number): void }) {
  const [text, setText] = React.useState<string | null>(null);
  return <input className="share-in" type="number" inputMode="numeric" min={1} max={max} step={1} aria-label={label} value={text ?? String(value)}
    onChange={e => { setText(e.target.value); const n = Number(e.target.value); if (e.target.value !== '' && Number.isInteger(n) && n >= 1 && n <= max) onChange(n); }}
    onBlur={() => setText(null)} />;
}
